import { useMediaQuery } from "@mui/material";
import { red } from "@mui/material/colors";
import { createTheme, useTheme } from "@mui/material/styles";
import { createMakeAndWithStyles } from "tss-react";

import PlayfairDisplayFont from "../assets/fonts/PlayfairDisplay-VariableFont_wght.ttf";
import PublicSansFont from "../assets/fonts/PublicSans-VariableFont_wght.ttf";

export const extraThemeOpts = {
  footerHeight: 72,
  mobileFooterHeight: 56,
  sidebarWidth: 300,
  sidebarMobileHeight: 90,
  sidebarMobilePadding: 8,
  participantBorderWidth: 2,
  mobileTopBarHeight: 52,
  rightDrawerWidth: 320,
  mobileScalingFactor: 0.65,
  iconScalingFactor: 0.15,
  drawerWidth: 200,
  verticalSpacing: {
    xl: "100px",
    l: "60px",
    m: "40px",
    std: "20px", // "standard"
    s: "15px",
    xs: "10px",
    t: "5px", // "tight"
  },
  layout: {
    main: {
      horizontalMargin: 86,
    },
  },
};

export type ExtraThemeOpts = typeof extraThemeOpts;

declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Theme extends ExtraThemeOpts {}
}

// A custom theme for this app
const THEME = createTheme({
  typography: {
    fontFamily: "PublicSansFont, Helvetica, Arial, sans-serif",
  },
  palette: {
    primary: {
      main: "#1A7E70",
      dark: "#146F63",
    },
    secondary: {
      main: "#19857b",
    },
    error: {
      main: red.A400,
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          backgroundColor: "#EEEAE0",
          fontSize: "14px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            outline: "none",
          },
          "&:focus": {
            outline: "none",
          },
          "&:active": {
            outline: "none",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            outline: "none",
          },
          "&:focus": {
            outline: "none",
          },
          "&:active": {
            outline: "none",
          },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          "&:hover": {
            outline: "none",
          },
          "&:focus": {
            outline: "none",
          },
          "&:active": {
            outline: "none",
          },
        },
      },
    },
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: `
        @font-face {
          font-family: 'Playfair Display';
          src: url(${PlayfairDisplayFont});
        }
        @font-face {
          font-family: 'PublicSansFont';
          src: url(${PublicSansFont});
        }
        `,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "inherit",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: "inherit",
        },
        icon: {
          color: "inherit",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: "inherit",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        *::-webkit-scrollbar {
          width: 10px;
        }
        *::-webkit-scrollbar-thumb {
          background: #606971;
          border-radius: 10px;
        }
        *::-webkit-scrollbar-track {
          background: transparent;
        }
        & textarea::-webkit-scrollbar {
          width: 10px;
        }
        & textarea::-webkit-scrollbar-thumb {
          background: #606971;
          borderRadius: 10px;
          outline: "none";
        }
        & textarea::-webkit-scrollbar-track {
          background: transparent;
        }
      `,
    },
  },
  ...extraThemeOpts,
});

export const { makeStyles, withStyles } = createMakeAndWithStyles({ useTheme });

export function useScreenSize() {
  const theme = useTheme();

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const isLarge = useMediaQuery(theme.breakpoints.down("lg"));

  return { isSmall, isMedium, isLarge };
}

export default THEME;
